import React, { memo, useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import axios from "axios";


import { BASE_URL } from "../../../../../../BaseUrl";
import { _t, getCookie, isValidPkMobile } from "../../../../../../functions/Functions";
import { ToastError, ToastSuccess } from "../../../../../../functions/ToastShow";
import { useTranslation } from "react-i18next";

const EMPTY_CUSTOMER = {
  id: null,
  name: "",
  email: "",
  phn_no: "",
  address: "",
};

const OrderCustomerDetail = ({
  orderDetails,
  isCustomerUpdateModal,
  setIsCustomerUpdateModal,
  onCallBackSuccess,
}) => {
  const { t } = useTranslation();

  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchLoader, setSearchLoader] = useState(false);
  const [noOptionMessage, setNoOptionMessage] = useState("Enter Phone No To Search");



  const onCloseModal=()=>{
    setIsCustomerUpdateModal(false);
    setCustomer(EMPTY_CUSTOMER);
    setSearchedCustomers([]);
    setSearchInput("");
    setSearchLoader(false);
    setNoOptionMessage("Enter Phone No To Search");
  }

  useEffect(() => {
    if (!isCustomerUpdateModal || !orderDetails?.item) return;

    const item = orderDetails.item;
    setCustomer({
      id: item?.customer_id || null,
      name: item?.customer_name || "",
      phn_no: item?.customer_phn_no || item?.delivery_phn_no || "",
      email: item?.customer_email || item?.email || "",
      address: item?.customer_address || item?.delivery_address || "",
    });
  }, [isCustomerUpdateModal]);

  // Debounced phone search
  useEffect(() => {
    if (!searchInput) {
      setSearchedCustomers([]);
      setSearchLoader(false);
      setNoOptionMessage("Enter Phone No To Search");
      return;
    }

    setSearchLoader(true);
    const timer = setTimeout(fetchCustomers, 2000);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCustomers = async () => {
    try {
      const { data } = await axios.post(
        BASE_URL + "/settings/get-all-customer",
        { customer_phn_no: searchInput },
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
      const results = data?.[0] ?? [];
      setSearchedCustomers(results);
      setSearchLoader(false);
      if (results.length === 0) {
        setNoOptionMessage("No Customer Exist");
        setCustomer((prev) => ({ ...EMPTY_CUSTOMER, phn_no: searchInput }));
      }
    } catch {
      setSearchLoader(false);
      ToastError({ title: t("Please try again") });
    }
  };

  // Populate form when a customer is selected from search
  const handleCustomerSelect = (selected) => {
    if (!selected) return;

    const address =
      selected.address_book?.length > 0
        ? selected.address_book[0].address
        : selected.address || "";

    setCustomer({
      id: selected.id,
      name: selected.name || "",
      email: selected.email || "",
      phn_no: (selected?.phn_no),
      address,
    });
  };

  // Generic input change
  const handleInput = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  // Submit / confirm customer
  const handleSubmit = async (e) => {
    e.preventDefault();

    if(!customer?.name){
      ToastError({ title: t("Please enter customer name!") });
      return;
    }

    if (customer.phn_no && !isValidPkMobile(customer.phn_no)) {
      // ToastError({ title: t("Please enter valid phone no (03XXXXXXXXX)") });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        order_id: orderDetails?.item?.id,
        branch_id: orderDetails?.item?.branch_id,
        customerInfo: customer,
      };
      const resp = await axios.post(BASE_URL + "/settings/edit-order-customer", payload, 
        { headers: { Authorization: `Bearer ${getCookie()}` } });
      if(resp.status === 200){
        if (typeof onCallBackSuccess === "function") {
          onCallBackSuccess(customer);
        }
        ToastSuccess({ title: t("Customer updated successfully") });
        onCloseModal();
      }
    } catch (error) {
      ToastError({ title: t("Failed to update customer") });
      console.log(error);
    }
    setLoading(false);
  };

  return (
    <Modal
      show={isCustomerUpdateModal}
      onHide={onCloseModal}
      size="lg"
      aria-labelledby="customer-modal-title"
      centered
      zIndex={14999}
    >
      <Modal.Header closeButton className="customer-header">
        <Modal.Title id="customer-modal-title" className="customer-title">
          <h5 className="fw-bold m-0">Customer Update</h5>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="container-fluid px-0 pt-0">

          {/* Search */}
          <div className="row mb-3">
            <div className="col-6">
              <label className="col-form-label fw-bold">Search By Phone No</label>
              <Select
                options={searchedCustomers}
                getOptionLabel={(o) => o.name_phone}
                getOptionValue={(o) => o.id}
                classNamePrefix="select"
                className="table-text"
                onChange={handleCustomerSelect}
                onInputChange={(val) => setSearchInput(val.replace(/\D/g, ""))}
                maxMenuHeight="200px"
                placeholder="Search by phone number"
                isClearable
                noOptionsMessage={() =>
                  searchLoader ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    noOptionMessage
                  )
                }
              />
            </div>
          </div>

          {/* Customer Form */}
          <h5 className="fw-bold mb-2">Customer Details</h5>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-4">
                <label className="col-form-label fw-bold p-0">Name</label>
                <input
                  type="text"
                  className="form-control customer-input"
                  name="name"
                  placeholder="Name"
                  onChange={handleInput}
                  value={customer.name}
                />
              </div>
              <div className="col-4">
                <label className="col-form-label fw-bold p-0">Phone</label>
                <input
                  type="text"
                  maxLength={11}
                  className="form-control customer-input"
                  name="phn_no"
                  placeholder="03XXXXXXXXX"
                  onKeyDown={(e) => ["e", "E", "ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                  onFocus={() => {
                    if (!customer.phn_no) setCustomer((prev) => ({ ...prev, phn_no: "03" }));
                  }}
                  onChange={(e) => {
                    const value = e.target.value
                    setCustomer((prev) => ({ ...prev, phn_no: value }));
                  }}
                  value={customer.phn_no}
                />
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-6">
                <label className="col-form-label fw-bold p-0">Email</label>
                <input
                  type="email"
                  className="form-control customer-input"
                  name="email"
                  placeholder="Email"
                  onChange={handleInput}
                  value={customer.email}
                />
              </div>
        
            </div>

            <div className="row mt-2">
              <div className="col-12">
                <label className="col-form-label fw-bold p-0">Delivery Address</label>
                <input
                  type="text"
                  className="form-control customer-input"
                  name="address"
                  placeholder="Address"
                  onChange={handleInput}
                  value={customer.address}
                />
              </div>
            </div>


            <Modal.Footer style={{ borderTop: "none", paddingRight: 0, marginBottom: -15 }}>
              <div className="d-flex justify-content-end align-items-center gap-2 w-100">
                <button
                  type="button"
                  className="btn btn-sm btn-light border-1 border fw-bold rounded-25 px-3 py-1"
                  onClick={onCloseModal}
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-sm btn-primary fw-bold rounded-25 px-3 py-1"
                  >
                  {loading ? (
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    "CONFIRM"
                  )}
                </button>
              </div>
            </Modal.Footer>
          </form>

        </div>
      </Modal.Body>
    </Modal>
  );
};

export default memo(OrderCustomerDetail);